/**
 * @typedef {import('./baseComposer/baseComposer').FieldType}
 */
/**
 * @typedef {import('./baseComposer/baseComposer').FieldValueType}
 */
/**
 * @typedef {import('./baseComposer/baseComposer').BaseComposerProps}
 */
/**
 * @typedef {'draft'|'pend'|'progress'|'finish'|'cancel'} StatusType
 */
/**
 * @typedef {'low'|'middle'|'high'} LevelType
*/
/**
 * @typedef {40|60|80} PopupHeight
 */
/**
 * @typedef {'POP-UP'|'ServiceTicket'} CardType
 */
/**
 * @typedef {Object} CardInfo
 * @property {CardType} type
 * the type of this card
 * @property {StatusType} status
 * card status
 * @property {string} title
 * card title
 * @property {string} text
 * card text
 * @property {Language} language
 * cart language
 * @property {Array<BaseComposerProps>} table
 * card table, containing several fields and
 */

/**
 * @typedef {Object} FormatOptions
 * @property {Array<{label: string, value: any}>} [options]
 * @property {number} [maximum]
 * @property {number} [minimum]
 * @property {boolean} [editable]
 */

/**
 * @typedef {Object} BaseDraftCardProps
 * @property {string} [title]
 * title of card
 * @property {string} [text]
 * text of card
 * @property {StatusType} [status]
 * status of card, shown at top-right of card.
 * @property {LevelType} [level]
 * level type, only for service ticket
 * @property {string} [levelText]
 * @property {Array<BaseComposerProps>} [fields]
 * @property {string} [statusText='Undefined text']
 * the status text,
 * @property {string} [reviseText]
 * title of revise pop-up component
 * @property {PopupHeight} [height]
 * the height of revise pop-up component
 * @property {Language} [language]
 * language of this card, the fixed text will be changed automatically.
 * @property {function(CardInfo): void} submit
 * the submit function, it will be called after click the submit button.
 * @property {CardType} cardType
 * the type of this card
 * @property {boolean} editable
 * if true, revise button and submit button will be shown.
 */

import React, {useState} from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardText,
    Carousel,
    Divider,
    Flex,
    FlexItem,
    Icon, Image,
    Popup
} from "@chatui/core";
import BaseComposer from "./baseComposer/baseComposer";
import CardTitleWithStatus from "./cardTitleWithStatus";
import './baseDraftCard.css'
import HighlightText from "./highlightText";

/**
 *
 * @param {BaseDraftCardProps} props
 */
function BaseDraftCard(props) {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language === 'zh' ? 'zh_cn' : 'en_us';
    
    const {
        fields = [],
        style,
        fluid = true,
        size = undefined,
        children,
        serviceTicketExtraInfo= {},
        popExtraInfo = {},
        title = '',
        text = '',
        reviseTitle,
        height = 80,
        language = currentLanguage,
        submit = ()=>{console.warn('no given submit function')},
        cardType = 'POP-UP',
        editable = true,
        bottomInfo = [],
        popDesc=""
    } = props;

    // 如果没有传入 reviseTitle，使用翻译的默认值
    const defaultReviseTitle = reviseTitle || (language === 'zh_cn' ? t('baseDraftCard.review', '预览') : t('baseDraftCard.review', 'Review'));

    const [openFields, setOpenFields] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // for service ticket
    const initTable = {};
    for(let i in fields){
        initTable[fields[i].name] = fields[i].value;
    }
    const [table, setTable] = useState(initTable);

    // for pop
    const [popTitle, setPopTitle] = useState(title);
    const [popText, setPopText] = useState(text);
    const [popImage, setPopImage] = useState(popExtraInfo.image);

    const setValue = (fieldName, value) => {
        const newTable = {...table};
        newTable[fieldName] = value;
        setTable(newTable);
    };

    const onAcknowledge = () => {
        if (submit) {
            let cardInfo = {}
            if(cardType==="ServiceTicket") {
                cardInfo = {
                    title: title,
                    text: text,
                    language: language,
                    table: table,
                };
            }
            else if (cardType==="POP-UP") {
                cardInfo = {
                    title: popTitle,
                    text: popText,
                    language: language,
                    image: popImage
                }
            }
            submit(cardInfo)
                .then(()=>{
                    setHasSubmitted(true);
                })
                .catch(error=>{
                    console.log(error);
                });
        }
    };

    const renderCardTitle = () => {
        if (cardType==="ServiceTicket") {
            if (serviceTicketExtraInfo.status === 'draft')
                return (
                    <CardTitleWithStatus
                        title={title}
                        statusText={serviceTicketExtraInfo.statusText}
                    ></CardTitleWithStatus>
                )
            return (
                <div className={"service-ticket-title"}>
                    <Flex center className={serviceTicketExtraInfo.status + "-title-bg"}>
                        <FlexItem>
                            <Flex align={'center'} className={serviceTicketExtraInfo.level + "-level"}>
                                <div className={"level"}>{serviceTicketExtraInfo.levelText}</div>
                                <div className={'title-text'}>{title}</div>
                            </Flex>
                        </FlexItem>
                        <div className={"status-tag"}>
                            <div className={serviceTicketExtraInfo.status + "-tag-container"}>
                                <div className={"tag-text"}>{serviceTicketExtraInfo.statusText}</div>
                            </div>
                        </div>
                    </Flex>
                </div>
            );
        }
        if (cardType==="POP-UP") {
            return (
                <CardTitleWithStatus
                    title={popTitle}
                    statusText={popExtraInfo.statusText}
                ></CardTitleWithStatus>
            )
        }
        return null
    };

    const renderCardContent = () => {
        return (
            <CardContent>
                <div className={"card-text"}>
                    {cardType==="POP-UP"?(popText):(text)}
                </div>
                {renderBottomInfo()}
            </CardContent>
        )
    }

    const renderBottomInfo = () => {
        if (editable)
            return null
        return (
            <>
                {cardType==="ServiceTicket" && (
                    <>
                        <div className={'divider'} key="divider"></div>
                        <div className={'bottom-info'} key="tracking-number">{serviceTicketExtraInfo.trackingNumber.text}</div>
                    </>
                )}

                <div className={'bottom-info'} key="bottom-info">
                    <Flex>
                        {bottomInfo.map((item, index)=>(
                        <Flex align={'center'} key={item.icon || index}>
                            <Icon type={item.icon}></Icon>
                            {item.text}
                        </Flex>
                        ))}
                    </Flex>
                </div>
            </>
        )
    };

    const renderPopDescription = () => {
        return (
            <HighlightText>
                <Icon type={'smile'}></Icon>
                {popDesc}
            </HighlightText>
        )
    }


    const renderEditableArea = () => {
        if (editable) {
            let editableAreaClassName = undefined;
            let popDesc = null
            if (cardType==='POP-UP') {
                editableAreaClassName = "pop-table-area";
                popDesc = renderPopDescription();
            }
            return (
                <>
                    <Popup
                        title={reviseTitle || defaultReviseTitle}
                        active={openFields}
                        height={height}
                        backdrop={true}
                        showClose={true}
                        onClose={() => {
                            setOpenFields(false);
                        }}
                        onBackdropClick={() => {
                            setOpenFields(false);
                        }}
                    >
                        {popDesc}
                        <CardContent
                            className={editableAreaClassName}
                        >
                            {cardType==="ServiceTicket"?(
                                fields.map((field, index) => (
                                    <BaseComposer
                                        key={index}
                                        className={"base-composer"}
                                        name={field.name}
                                        type={field.type}
                                        value={table[field.name]}
                                        setValue={(value) => setValue(field.name, value)}
                                        format={field.format}
                                        description={field.description}
                                        language={language}
                                        children={children}
                                    ></BaseComposer>
                                ))
                            ):(
                                <>
                                    <BaseComposer
                                        className={"base-composer"}
                                        name={language==="zh_cn" ? t('baseDraftCard.image', '图片') : t('baseDraftCard.image', 'Image')}
                                        type={"image"}
                                        value={popImage}
                                        setValue={(value)=>setPopImage(value)}
                                        language={language}
                                    />
                                    <BaseComposer
                                        className={"base-composer"}
                                        name={language==="zh_cn" ? t('baseDraftCard.title', '标题') : t('baseDraftCard.title', 'Title')}
                                        type={"text"}
                                        value={popTitle}
                                        setValue={(value)=>setPopTitle(value)}
                                        language={language}
                                    />
                                    <BaseComposer
                                        className={"base-composer"}
                                        name={language==="zh_cn" ? t('baseDraftCard.content', '内容') : t('baseDraftCard.content', 'Content')}
                                        type={"textarea"}
                                        value={popText}
                                        setValue={(value)=>setPopText(value)}
                                        language={language}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Popup>

                    <CardActions onClick={(e) => e.stopPropagation()}>
                        <Button 
                            disabled={hasSubmitted} 
                            className={'revise-button'} 
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenFields(true);
                            }}
                        >
                            {language === 'zh_cn' ? t('baseDraftCard.review', '预览') : t('baseDraftCard.review', 'Review')}
                        </Button>
                        <Button 
                            disabled={hasSubmitted} 
                            color={'primary'} 
                            onClick={(e) => {
                                e.stopPropagation();
                                onAcknowledge();
                            }}
                        >
                            {language === 'zh_cn' ? t('baseDraftCard.submit', '提交') : t('baseDraftCard.submit', 'Submit')}
                        </Button>
                    </CardActions>
                </>
            )
        }
        return null
    }
    const renderImages = () => {
        if (cardType==="POP-UP")
            if (popImage.length>0)
                return (
                    <Carousel>
                        {popImage.map((url, index) => (
                            <Image src={url} key={index}/>
                        ))}
                    </Carousel>
                )
        return null
    }
    // 处理卡片点击（如果可编辑，点击卡片也可以打开编辑弹窗）
    const handleCardClick = (e) => {
        // 如果点击的是按钮区域，不触发卡片点击
        if (e.target.closest('.CardActions') || e.target.closest('button')) {
            return;
        }
        if (editable && !hasSubmitted) {
            setOpenFields(true);
        }
    };

    return (
        <div className={"base-draft-card-container"}>
            <Card 
                className={`base-draft-card ${editable && !hasSubmitted ? 'base-draft-card-clickable' : ''}`}
                style={style}
                fluid={fluid}
                size={size}
                onClick={editable && !hasSubmitted ? handleCardClick : undefined}
            >
                {renderImages()}
                {renderCardTitle()}
                {renderCardContent()}
                {renderEditableArea()}
            </Card>
        </div>
    );
}

export default BaseDraftCard;